import { Action, ActionReducer, ActionReducerMap, combineReducers } from '@ngrx/store';
import { hasNoValue, hasValue, isFunction, isNotEqual, toArray } from '@util/type';
import * as R from 'ramda';
import { compose, find, identity, includes, reduce } from 'ramda';

export type Reducer<S, A> = (state: S, action: A, ...params) => S;

/**
 * Interface for action handlers. Each action handler contains list of actions that should be handled and a
 * function that will handle those actions.
 * */
export interface ActionHandler<S, A> {
  action: string | string[];
  handler: Reducer<S, A>;
}

const actionProp = handler => handler.action;
const ofType = (actionType: string) => compose(includes(actionType), toArray, actionProp);

/**
 * Creates action reducer for given handlers.
 *
 * @param {ActionHandler<S, A>[]} handlers - list of action handlers
 * @param {S} initialState - initial state of the store
 * @return {Reducer<S, A>} reducer
 * */
export const createReducer = <S, A extends Action>(handlers: ActionHandler<S, A>[], initialState?: S): Reducer<S, A> =>
  (state: S = initialState, action: A, ...params): S => {
    const actionHandler = action && find(ofType(action.type), handlers);
    const handler = actionHandler ? actionHandler.handler : identity;
    return handler(state, action, ...params);
  };

/**
 * Creates specialized versions of a reducer function that will only respond to specific actions.
 *
 * The reducer will be called when:
 * - The state should be initialized
 * - The action contains controlId. Because we are using ngrx-forms library, each action has unique control id, that
 *   is why it is safe to call the reducer without checking for payload type.
 * - The payload type matches the reducer type
 *
 * @param {Reducer} reducer - a reducer
 * @param {string} type - reducer type used for filtering actions by payload type
 * @return {Reducer} reducer that responds only to specific actions
 */
export const createTypedReducer = <S, A extends Action>(reducer: Reducer<S, A>, type) =>
  (state, action) => {
    const isInitializationCall = R.isNil(state);
    const {controlId, payload} = action;
    const isDifferentType = hasNoValue(payload) || isNotEqual(payload.type, type);

    if (isInitializationCall || hasValue(controlId) || !isDifferentType) {
      return reducer(state, action);
    }

    return state;
  };

export const createHandler = (action, handler) => ({
  action,
  handler
});

export const executeReducers = (state, action, reducers): any =>
  reduce((nextState, reducer: any) => reducer(nextState, action), state, reducers);

const toHandler: any = defaultHandlers => ([handler, action]) => createHandler(action, defaultHandlers[handler]);
export const createHandlers: any = defaultHandlers => R.compose(R.map(toHandler(defaultHandlers)), R.toPairs);

const resetReducer = R.curry((resetAction: string, reducer) => (state, action) =>
  action && action.type === resetAction
    ? reducer(undefined, action)
    : reducer(state, action)
);

export const reducerWithResetAction = <T>(resetAction: string, reducers: ActionReducerMap<T> | ActionReducer<T>) =>
  isFunction(reducers)
    ? resetReducer(resetAction, reducers)
    : resetReducer(resetAction)(combineReducers(reducers));
